import React from "react";
import { DashboardLayout } from "component/layout/dashboard";
import * as Mui from "@mui/material";
import { withStyles } from "tss-react/mui";
import type { WithStyles } from "type/tss";
import { LeadDetails } from "./lead-details";
import { NotesCard } from "./notes-card";
import { SubscriptionsTab } from "./subscriptions-tab";
import { HistoryTab } from "./history-tab";
import { PropertyOrganizerTab } from "./property-organizer-tab";
import { SearchAgentTab } from "./search-agent-tab";
import { SparkleIcon } from "./search-agent-tab/icons";
import { TabBadge } from "./tab-badge";
import { connect } from "react-redux";
import { bindActionCreators, Dispatch } from "redux";
import { RootState } from "redux/store";
import { getLeadById, getLeadProfileSuggestionCountForLead } from "redux/selector";
import { fetchLeadProfileSuggestions } from "redux/slice/search-agent";
import { canReviewLeadProfileSuggestions } from "shared/lead-profile-suggestions-permission";
import { styles } from "./style";
import { LeadAvatar } from "component/shared/lead-avatar";
import { NotFoundPage } from "component/page/not-found";
import { PageProps } from "shared/page-props";
import * as Router from "react-router-dom";
import { LoadingPage } from "../loading";
import { withMediaQuery, WithMediaQuery } from "component/shared/with-media-query";
import { UnderlineTabs } from "component/shared/underline-tabs";
import { getBookmarkTagForLead } from "redux/selector";
import { getTasksForLead } from "redux/selector";
import { fetchLeadActivities } from "redux/slice/lead-activities";
import { fetchEmailSents } from "redux/slice/email-sents";
import { fetchEmailClicks } from "redux/slice/email-clicks";
import { fetchSavedListings } from "redux/slice/saved-listings";
import { Pipeline } from "./pipeline";
import { LeadNavigationBar } from "./lead-navigation-bar";
import { Bookmark } from "component/shared/bookmark";
import { TasksCard } from "./tasks-card";
import { Lead } from "model/lead";
import { Confetti } from "component/shared/confetti";
import { LeadActivityFilter } from "../lead-activity/lead-activity-filter";
import { mixpanelAnalytics } from "shared/mixpanel-analytics";
import { hasSearchAgent } from "shared/search-agent-permission";
import { getLeadTabIndex } from "type/lead-tab";
import queryString from "query-string";

const mapStateToProps = (
	state: RootState,
	ownProps: Router.RouteComponentProps<RouteParams>
) => {
	const leadId = getId(ownProps);
	return {
		lead: getLeadById(state, leadId) as Lead,
		loading: state.leads.loading,
		bookmarkTag: getBookmarkTagForLead(state, leadId),
		tasks: getTasksForLead(state, leadId),
		profileSuggestionCount: getLeadProfileSuggestionCountForLead(state, leadId),
	};
};

const mapDispatchToProps = (dispatch: Dispatch) => bindActionCreators({
	fetchLeadActivities,
	fetchEmailSents,
	fetchEmailClicks,
	fetchSavedListings,
	fetchLeadProfileSuggestions,
}, dispatch);

interface Props extends
	ReturnType<typeof mapStateToProps>,
	ReturnType<typeof mapDispatchToProps>,
	PageProps,
	Router.RouteComponentProps<RouteParams>,
	WithStyles<typeof styles>,
	WithMediaQuery
{
}

interface State {
	tabIndex: number;
	confetti: boolean;
}

interface RouteParams {
	id: string;
}

interface Tab {
	label: string;
	view: React.ReactElement;
	/** Rendered before the label — the Search Agent tab carries the AI sparkle. */
	icon?: React.ReactElement;
	/** A count rendered after the label, hidden at zero. */
	badge?: number;
}

const getId = (props: Router.RouteComponentProps<RouteParams>) => {
	return parseInt(props.match.params.id);
};

class Component extends React.Component<Props, State> {
	constructor(props: Props) {
		super(props);
		this.state = {
			tabIndex: this.getRequestedTabIndex() ?? 0,
			confetti: false,
		};
	}

	public componentDidMount() {
		this.loadLead();
	}

	private loadLead() {
		const { user } = this.props;
		const leadId = getId(this.props);
		mixpanelAnalytics.trackViewLeadDetail(leadId);
		this.props.fetchLeadActivities({leadId});
		if (user.permissions.marketReport || user.permissions.leadSaveSearch) {
			this.props.fetchEmailSents({leadId});
			this.props.fetchEmailClicks({leadId});
		}
		if (user.permissions.leadSaveListing) {
			this.props.fetchSavedListings({leadId});
		}
		// Fetched with the page rather than the tab, so the tab's badge is right before it is opened.
		if (canReviewLeadProfileSuggestions(user)) {
			this.props.fetchLeadProfileSuggestions({ leadId });
		}
	}

	public componentDidUpdate(prevProps: Props) {
		// A link to another tab of the lead already open, e.g. from a card on its Activity tab
		if (prevProps.location.search !== this.props.location.search) {
			const tabIndex = this.getRequestedTabIndex();
			if (tabIndex !== undefined) {
				this.setState({ tabIndex });
			}
		}
		// Previous/Next navigation reuses this component for a different lead
		if (getId(prevProps) !== getId(this.props)) {
			this.setState({ confetti: false });
			window.scrollTo(0, 0);
			this.loadLead();
			return;
		}
		if (prevProps.lead && this.props.lead && this.props.lead.pipeline) {
			const { pipeline: prevPipeline } = prevProps.lead;
			const { pipeline } = this.props.lead;
			if ((!prevPipeline || (!prevPipeline.type.underContract && !prevPipeline.type.closed)) && (pipeline.type.underContract || pipeline.type.closed)) {
				this.setState({ confetti: true });
			}
			if ((prevPipeline && (prevPipeline.type.underContract || prevPipeline.type.closed)) && (!pipeline.type.underContract && !pipeline.type.closed)) {
				this.setState({ confetti: false });
			}
		}
	}

	private handleTabChange = (newTabIndex: number) => {
		const { lead } = this.props;
		const tab = this.tabs[newTabIndex];

		if (tab.label === "Activity") {
			mixpanelAnalytics.trackViewActivity(lead.id);
		} else if (tab.label === "Subscriptions") {
			mixpanelAnalytics.trackViewSubscriptions(lead.id);
		} else if (tab.label === "Property Organizer") {
			mixpanelAnalytics.trackViewPropertyOrganizer(lead.id);
		} else if (tab.label === "Search Agent") {
			mixpanelAnalytics.trackViewSearchAgent(lead.id);
		}

		this.setState({ tabIndex: newTabIndex });
		// Drop the tab the page was linked to, so that following the same link again changes the URL
		const { history, location } = this.props;
		const { tab: linkedTab, ...query } = queryString.parse(location.search);
		if (linkedTab !== undefined) {
			history.replace({ ...location, search: queryString.stringify(query) });
		}
	}

	/** The tab named by `?tab=`, when the user can see it. */
	private getRequestedTabIndex() {
		return getLeadTabIndex(this.props.location.search, this.tabs.map((tab) => tab.label));
	}

	private getSubscriptionTabPermission() {
		const { user } = this.props;
		if (user.admin) {
			return true;
		}
		return user.permissions.modifySubscriptions && (
			user.permissions.campaigns || 
			user.permissions.report || 
			user.permissions.leadEmailUpdates
		);
	}

	private showTab(label: string) {
		const tabIndex = this.tabs.findIndex((tab) => tab.label === label);
		if (tabIndex >= 0) {
			this.setState({ tabIndex });
		}
	}

	private getSearchAgentTabPermission() {
		return hasSearchAgent(this.props.user);
	}

	private get tabs(): Tab[] {
		const { lead, user, profileSuggestionCount, classes = {} } = this.props;
		let tabs: Tab[] = [];
		tabs.push({ label: "Activity", view: <LeadActivityFilter lead={lead} isAgentPage /> });
		if (this.getSearchAgentTabPermission()) {
			tabs.push({
				label: "Search Agent",
				icon: <SparkleIcon className={classes.tabIcon} />,
				badge: canReviewLeadProfileSuggestions(user) ? profileSuggestionCount : undefined,
				view: <SearchAgentTab lead={lead} />,
			});
		}
		if (this. getSubscriptionTabPermission()) {
			tabs.push({
				label: "Subscriptions",
				view: (
					<SubscriptionsTab
						lead={lead}
						showPropertyOrganizer={() => this.showTab("Property Organizer")}
						user={user}
					/>
				),
			});
		}
		if (user.permissions.leadPropertyOrganizer) {
			tabs.push({
				label: "Property Organizer",
				view: <PropertyOrganizerTab lead={lead} user={user} />,
			});
		}
		if (user.permissions.crmNotes) {
			tabs.push({
				label: "Notes",
				view: <HistoryTab lead={lead} user={user} />,
			});
		}

		return tabs;
	}

	public render() {
		const { user, lead, loading, classes = {} } = this.props;
		const { tabIndex, confetti } = this.state;
		if (!lead) {
			if (loading) {
				return <LoadingPage />;
			} else {
				return <NotFoundPage />;
			}
		}
		const title = lead.label;
		const isMobile = this.props.mediaQuery;

		return (
			<DashboardLayout
				permitted={user && user.permissions.leads}
				title={title}
				header={
					<div className={classes.header}>
						<Mui.Typography variant="h1">
							{title}
						</Mui.Typography>
						{user.permissions.crmTags && <Bookmark leadId={lead.id} showLabel={true} />}
					</div>
				}
			>
				<LeadNavigationBar leadId={lead.id} />
				{confetti && (
					<Confetti
						onStop={() => this.setState({ confetti: false })}
					/>
				)}
				{user.permissions.crmPipeline && (
					<div className={classes.pipelineSection} key={`pipeline-${lead.id}`}>
						<Mui.Divider className={classes.pipelineDividerTop} />
						<Pipeline lead={lead} />
						<Mui.Divider className={classes.pipelineDividerBottom} />
					</div>
				)}
				{/* keyed so per-lead child state resets on Previous/Next */}
				<Mui.Grid container item spacing={2} key={lead.id}>
					<Mui.Grid
						container
						item
						xs={12}
						lg={4}
						spacing={2}
						alignContent="flex-start"
					>
						<Mui.Grid item xs={12} md={8} lg={12}>
							<LeadDetails user={user} lead={lead} />
						</Mui.Grid>
						{user.permissions.crmTasks && (
							<Mui.Grid item xs={12} md={8} lg={12}>
								<TasksCard lead={lead} />
							</Mui.Grid>
						)}
						{!user.permissions.crmNotes && (
							<Mui.Grid item xs={12}>
								<NotesCard lead={lead} />
							</Mui.Grid>
						)}
					</Mui.Grid>
					<Mui.Grid item xs={12} lg={8} spacing={2} alignContent="flex-start">
						<Mui.Grid item style={{ width: "100%" }}>
							<UnderlineTabs
								aria-label="Lead profile"
								tabs={this.tabs.map((tab, index) => ({
									value: index,
									label: tab.label,
									icon: tab.icon,
									adornment: !!tab.badge && <TabBadge count={tab.badge} />,
								}))}
								value={tabIndex}
								onChange={this.handleTabChange}
								scrollable={isMobile}
							/>
						</Mui.Grid>
						<Mui.Grid item style={{ height: 25 }} />
						<Mui.Grid item>{this.tabs[tabIndex].view}</Mui.Grid>
					</Mui.Grid>
				</Mui.Grid>
			</DashboardLayout>
		);
	}
}

export const LeadPage = withMediaQuery("(max-width:600px)")(
	withStyles(connect(
		mapStateToProps,
		mapDispatchToProps,
	)(Component), styles),
);